import React from 'react';
import { X } from 'lucide-react';

interface FileViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileUrl: string;
  fileName: string;
  fileType: 'pdf' | 'image';
}

const ImageViewer: React.FC<{ imageUrl: string; fileName: string }> = ({ imageUrl, fileName }) => {
  const [error, setError] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  
  return (
    <div className="flex items-center justify-center p-2 sm:p-4">
      {error ? (
        <div className="text-center p-4 sm:p-8">
          <p className="text-red-600 mb-4 text-sm sm:text-base">Unable to display image</p>
          <a 
            href={imageUrl} 
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg text-sm sm:text-base hover:bg-blue-700"
          >
            Open Image in New Tab
          </a>
        </div>
      ) : (
        <>
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}
          <img 
            src={imageUrl} 
            alt={fileName}
            style={{ maxWidth: '100%', maxHeight: '70vh', height: 'auto' }}
            className="rounded-lg shadow-lg"
            onLoad={() => setLoading(false)}
            onError={() => {
              console.error('Image failed to load:', imageUrl);
              setError(true);
              setLoading(false);
            }}
          />
        </>
      )}
    </div>
  );
};

const PDFViewer: React.FC<{ fileUrl: string }> = ({ fileUrl }) => {
  const [error, setError] = React.useState(false);
  
  return (
    <div className="w-full h-full flex items-center justify-center">
      {error ? (
        <div className="text-center p-4 sm:p-8">
          <p className="text-red-600 mb-4 text-sm sm:text-base">Unable to display PDF in browser</p>
          <a 
            href={fileUrl} 
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg text-sm sm:text-base hover:bg-blue-700"
          >
            Open PDF in New Tab
          </a>
        </div>
      ) : (
        <>
          <iframe
            src={`${fileUrl}#toolbar=1&navpanes=1&scrollbar=1`}
            className="w-full h-[60vh] sm:h-[70vh] lg:h-[80vh] border-0"
            title="PDF Viewer"
            onError={() => setError(true)}
            onLoad={(e) => {
              // Check if iframe loaded successfully
              const iframe = e.target as HTMLIFrameElement;
              try {
                if (!iframe.contentDocument && !iframe.contentWindow) {
                  setError(true);
                }
              } catch {
                // Cross-origin error, try alternative
                setError(true);
              }
            }}
          />
          {/* Fallback link always visible for PDFs */}
          <div className="absolute bottom-4 right-4">
            <a 
              href={fileUrl} 
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 shadow-lg"
            >
              Open in New Tab
            </a>
          </div>
        </>
      )}
    </div>
  );
};

const FileViewerModal: React.FC<FileViewerModalProps> = ({ isOpen, onClose, fileUrl, fileName, fileType }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-75 p-2 sm:p-4">
      <div className="relative bg-white rounded-lg sm:rounded-2xl shadow-2xl max-w-6xl max-h-[95vh] sm:max-h-[90vh] w-full overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-3 sm:p-4 border-b bg-gray-50">
          <h3 className="text-sm sm:text-lg font-semibold text-gray-900 truncate pr-2">{fileName}</h3>
          <button 
            onClick={onClose} 
            className="p-1 sm:p-2 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto bg-gray-100 relative">
          {fileType === 'image' ? <ImageViewer imageUrl={fileUrl} fileName={fileName} /> : <PDFViewer fileUrl={fileUrl} />}
        </div>
      </div>
    </div>
  );
};

export default FileViewerModal;
